import {objectFromEntries} from '@benchling/js-utils';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  NON_PERSISTED_GQL_FIELDS_BY_CHART_TYPE,
  NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE,
} from 'analysis-tool/charting/AnalysisChartingConstants';
import {DataFrameFilterStepConfig} from 'data-frames/filters/DataFrameFilterTypes';
import {
  PipelineStepType,
  AnalyticsVisualizationAggregationType as AggregationType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';

/**
 * We don't save certain visualization config fields in the visualization config of charts created in the
 * analysis tool, since they are graphql implementation details (i.e. `__typename`) or they are supported in a
 * different way (e.g. `aggregationType` would be represented by another pipeline step prior to the
 * visualization step), so we add them in here with null values in order to have a valid visualization config.
 */
export function getVisualizationConfigGQLFromStep(step: GetAnalysisPipelineStep): {
  visualizationConfig: VisualizationConfig;
  visualizationName: string;
} {
  const config = getPipelineStepConfig(step, PipelineStepType.VISUALIZATION, 0);
  return {
    visualizationConfig: {
      __typename: 'AnalyticsVisualizationNode',
      id: `stub-id-${step.id}`,
      parameters: {
        __typename: VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME[config.visualizationConfig.type],
        ...config.visualizationConfig.parameters,
        ...objectFromEntries(
          NON_PERSISTED_GQL_FIELDS_BY_CHART_TYPE[config.visualizationConfig.type].map((field) => [
            field,
            null,
          ])
        ),
      } as AnalyticsVisualization['parameters'],
    },
    visualizationName: config.visualizationName,
  };
}

export function getVisualizationConfigGQLFromChartStep(step: GetAnalysisPipelineStep): {
  visualizationConfig: VisualizationConfig;
  visualizationName: string;
} {
  const config = getPipelineStepConfig(step, PipelineStepType.CHART_VIEW, 0);
  return {
    visualizationConfig: {
      __typename: 'AnalyticsVisualizationNode',
      id: `stub-id-${step.id}`,
      parameters: {
        __typename: VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME[config.chartConfig.type],
        ...config.chartConfig.parameters,
        ...objectFromEntries(
          NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE[config.chartConfig.type].map((field) => [
            field,
            null,
          ])
        ),
        // aggregation is not a chart configuration in CHART_VIEW steps, and default behavior is to combine
        // rows by summing the values of the measure column. Also since we can't condition the measure column
        // selector on aggregation value, we need to stub a non-count value in here.
        ...(config.chartConfig.type === VisualizationType.PIE_CHART
          ? {
              aggregationType: AggregationType.SUM,
            }
          : {}),
      } as AnalyticsVisualization['parameters'],
    },
    visualizationName: config.chartName,
  };
}

export function getFilterConfigFromStep(step: GetAnalysisPipelineStep): DataFrameFilterStepConfig {
  return getPipelineStepConfig(
    step,
    PipelineStepType.FILTER_DATASET,
    0
  ) as unknown as DataFrameFilterStepConfig;
}
